import { FC, useEffect } from 'react';
import { Edit2, Save } from 'lucide-react';
import { Button, Input, SearchableSelect, SearchableOption, Flag } from '@/components/ui';
import { UserProfile, ProfileFormData, FormErrors } from '../types';
import { useLocation } from '../hooks/useLocation';
import { usePassword } from '../hooks/usePassword';

interface EmailPasswordTabProps {
    profile: UserProfile;
    formData: ProfileFormData;
    formErrors: FormErrors;
    isEditing: boolean;
    isSaving: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    onInputChange: (field: keyof ProfileFormData, value: string) => void;
}

export const EmailPasswordTab: FC<EmailPasswordTabProps> = ({
    profile,
    formData,
    formErrors,
    isEditing,
    isSaving,
    onEdit,
    onCancel,
    onSave,
    onInputChange,
}) => {
    const { countries, cities, isLoadingCities, selectedCountryCode, handleCountryChange } = useLocation(formData.country);

    const {
        currentPassword, setCurrentPassword,
        newPassword, setNewPassword,
        confirmPassword, setConfirmPassword,
        passwordError, passwordSuccess,
        isChangingPassword, handlePasswordChange
    } = usePassword();

    // Sync country code
    useEffect(() => {
        if (formData.country) {
            handleCountryChange(formData.country);
        }
    }, [formData.country]);

    return (
        <div className="space-y-6">
            {/* Header with Actions */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Email & Password</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage your email and password settings</p>
                </div>
                {!isEditing ? (
                    <Button onClick={onEdit} variant="secondary" size="md">
                        <Edit2 className="w-4 h-4" />
                        <span className="ml-2">Edit</span>
                    </Button>
                ) : (
                    <div className="flex gap-3">
                        <Button onClick={onCancel} variant="ghost" size="md" disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={onSave}
                            variant="primary"
                            size="md"
                            isLoading={isSaving}
                            disabled={isSaving || Object.values(formErrors).some((error) => error !== undefined)}
                        >
                            <Save className="w-4 h-4" />
                            <span className="ml-2">Save Changes</span>
                        </Button>
                    </div>
                )}
            </div>

            {/* Email Section */}
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">Email Address</h3>
                </div>
                <div className="p-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Email</label>
                        <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 rounded-md border border-gray-200">
                            {profile.email}
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                            Email address is linked to your account and cannot be changed
                        </p>
                    </div>
                </div>
            </div>

            {/* LinkedIn URL */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn Profile</label>
                {isEditing ? (
                    <div>
                        <Input
                            type="url"
                            value={formData.linkedinUrl}
                            onChange={(e) => onInputChange('linkedinUrl', e.target.value)}
                            placeholder="https://linkedin.com/in/yourprofile"
                            className={formErrors.linkedinUrl ? 'border-red-500' : ''}
                        />
                        {formErrors.linkedinUrl && (
                            <p className="text-xs text-red-600 mt-1.5">{formErrors.linkedinUrl}</p>
                        )}
                    </div>
                ) : (
                    <div className="px-3 py-2 text-sm text-gray-900">
                        {profile.linkedinUrl ? (
                            <a
                                href={profile.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                            >
                                {profile.linkedinUrl}
                            </a>
                        ) : (
                            <span className="text-gray-400">Not provided</span>
                        )}
                    </div>
                )}
            </div>

            {/* Address Information */}
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">Address Information</h3>
                </div>
                <div className="p-6 space-y-5">
                    {/* Street Address */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                        {isEditing ? (
                            <div>
                                <Input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => onInputChange('address', e.target.value)}
                                    placeholder="Enter your street address"
                                    className={formErrors.address ? 'border-red-500' : ''}
                                />
                                {formErrors.address && (
                                    <p className="text-xs text-red-600 mt-1.5">{formErrors.address}</p>
                                )}
                            </div>
                        ) : (
                            <div className="px-3 py-2 text-sm text-gray-900">
                                {profile.address || <span className="text-gray-400">Not provided</span>}
                            </div>
                        )}
                    </div>

                    {/* City & Country */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                            {isEditing ? (
                                <SearchableSelect
                                    options={countries.map((country): SearchableOption => ({
                                        value: country.name,
                                        label: country.name,
                                        icon: <Flag code={country.code} className="w-5 h-4 object-cover rounded" />,
                                        subtitle: `${country.currency} ${country.currencySymbol}`,
                                    }))}
                                    value={formData.country}
                                    onChange={(value) => onInputChange('country', value)}
                                    placeholder="Search for a country..."
                                    emptyMessage="No countries found"
                                    error={formErrors.country}
                                />
                            ) : (
                                <div className="px-3 py-2 text-sm text-gray-900">
                                    {profile.country ? (
                                        <div className="flex items-center gap-2">
                                            {countries.find((c) => c.name === profile.country) && (
                                                <Flag
                                                    code={countries.find((c) => c.name === profile.country)?.code || ''}
                                                    className="w-5 h-4 object-cover rounded"
                                                />
                                            )}
                                            <span>{profile.country}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">Not provided</span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                            {isEditing ? (
                                <SearchableSelect
                                    options={cities.map((city): SearchableOption => ({
                                        value: city.name,
                                        label: city.name,
                                        icon: city.isCapital ? '⭐' : '📍',
                                        subtitle: city.stateName,
                                    }))}
                                    value={formData.city}
                                    onChange={(value) => onInputChange('city', value)}
                                    placeholder={!selectedCountryCode ? 'Select a country first' : 'Search for a city...'}
                                    emptyMessage={!selectedCountryCode ? 'Please select a country first' : 'No cities found'}
                                    disabled={!selectedCountryCode}
                                    loading={isLoadingCities}
                                    error={formErrors.city}
                                />
                            ) : (
                                <div className="px-3 py-2 text-sm text-gray-900">
                                    {profile.city || <span className="text-gray-400">Not provided</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Section */}
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">Change Password</h3>
                    <p className="text-sm text-gray-500 mt-1">Update your password to keep your account secure</p>
                </div>
                <div className="p-6">
                    <form onSubmit={handlePasswordChange} className="space-y-5">
                        {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
                        {passwordSuccess && <p className="text-sm text-green-600">{passwordSuccess}</p>}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Current Password <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Enter your current password"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                New Password <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min. 8 characters)"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm New Password <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your new password"
                                required
                            />
                        </div>

                        <div className="pt-4">
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                isLoading={isChangingPassword}
                                disabled={isChangingPassword}
                            >
                                Update Password
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
